import React, { useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { updateCandidateDocuments } from '../../services/candidateService';
import { CANDIDATE_DOCUMENT_FIELDS } from '../../utils/apiAssetUrl';
import { openCandidateDocument } from '../../utils/openCandidateDocument';
import { getApiErrorMessage } from '../../utils/apiError';
import { showError, showSuccess } from '../../utils/toast';

function DocumentRow({
  label,
  storedPath,
  fieldName,
  candidateId,
  inputRef,
  uploading,
  canUpload,
  onUpload,
  onView,
  viewLabel,
}) {
  const hasFile = typeof storedPath === 'string' && storedPath.length > 0;
  const [viewLoading, setViewLoading] = useState(false);

  const handleView = async () => {
    if (!hasFile || !candidateId) return;
    setViewLoading(true);
    try {
      await onView(candidateId, fieldName);
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <tr>
      <td><strong>{label}:</strong></td>
      <td>
        {hasFile ? (
          <div style={{ marginBottom: '8px' }}>
            <Button
              variant="link"
              className="p-0 align-baseline"
              disabled={viewLoading}
              onClick={handleView}
            >
              {viewLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Opening…
                </>
              ) : (
                viewLabel
              )}
            </Button>
          </div>
        ) : (
          <div className="text-muted" style={{ marginBottom: '8px' }}>No PDF available.</div>
        )}
        {canUpload ? (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Control
              ref={inputRef}
              type="file"
              accept="application/pdf"
              disabled={uploading}
              style={{ maxWidth: '280px' }}
            />
            <Button
              variant="outline-primary"
              size="sm"
              disabled={uploading}
              onClick={() => onUpload(fieldName)}
            >
              {uploading ? 'Uploading…' : hasFile ? 'Replace PDF' : 'Upload PDF'}
            </Button>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

function SourceProfileDocumentFields({
  candidateId,
  updatedResume = '',
  ornnovaProfile = '',
  canUpload = false,
  onDocumentsUpdated,
}) {
  const resumeRef = useRef(null);
  const ornnovaRef = useRef(null);
  const [uploadingField, setUploadingField] = useState('');

  const uploadField = async (fieldName) => {
    if (!candidateId) return;
    const inputRef = fieldName === CANDIDATE_DOCUMENT_FIELDS.resume ? resumeRef : ornnovaRef;
    const file = inputRef.current?.files?.[0];
    if (!file) {
      showError('Please choose a PDF file first.');
      return;
    }
    if (file.type !== 'application/pdf') {
      showError('Only PDF files are allowed.');
      return;
    }

    const formData = new FormData();
    formData.append(fieldName, file);
    setUploadingField(fieldName);
    try {
      const data = await updateCandidateDocuments(candidateId, formData);
      showSuccess('Document updated successfully ✓');
      if (inputRef.current) inputRef.current.value = '';
      onDocumentsUpdated?.(data.candidate);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Failed to upload document. Please try again.'));
    } finally {
      setUploadingField('');
    }
  };

  if (!candidateId) return null;

  return (
    <>
      <DocumentRow
        label="Source Profile Resume"
        storedPath={updatedResume}
        fieldName={CANDIDATE_DOCUMENT_FIELDS.resume}
        candidateId={candidateId}
        inputRef={resumeRef}
        uploading={uploadingField === CANDIDATE_DOCUMENT_FIELDS.resume}
        canUpload={canUpload}
        onUpload={uploadField}
        onView={openCandidateDocument}
        viewLabel="View Resume"
      />
      <DocumentRow
        label="Ornnova Profile"
        storedPath={ornnovaProfile}
        fieldName={CANDIDATE_DOCUMENT_FIELDS.ornnovaProfile}
        candidateId={candidateId}
        inputRef={ornnovaRef}
        uploading={uploadingField === CANDIDATE_DOCUMENT_FIELDS.ornnovaProfile}
        canUpload={canUpload}
        onUpload={uploadField}
        onView={openCandidateDocument}
        viewLabel="View Ornnova Profile"
      />
    </>
  );
}

export default SourceProfileDocumentFields;
